import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
  Snackbar,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';

const CashEntryDetailsDialog = ({
  open,
  onClose,
  businessDate,
  storeId,
  glAccount,
  memoDetail,
  firebaseUser,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState([]);
  const [showCashGuid, setShowCashGuid] = useState(false);
  const [orderBy, setOrderBy] = useState('amount');
  const [order, setOrder] = useState('desc');
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});

  const handleCopy = () => {
    setCopySnackbarOpen(true);
  };

  const handleColumnFilterChange = (columnId, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [columnId]: value,
    }));
  };

  const clearColumnFilter = (columnId) => {
    setColumnFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[columnId];
      return newFilters;
    });
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredDetails = useMemo(() => {
    if (!details.length) return details;
    const activeFilters = Object.entries(columnFilters).filter(([_, value]) => value.trim() !== '');
    if (activeFilters.length === 0) return details;
    return details.filter((row) => {
      return activeFilters.every(([columnId, filterValue]) => {
        const cellValue = String(row[columnId] ?? '').toLowerCase();
        const filterLower = filterValue.toLowerCase();
        return cellValue.includes(filterLower);
      });
    });
  }, [details, columnFilters]);

  const sortedDetails = useMemo(() => {
    if (!filteredDetails.length) return filteredDetails;

    return [...filteredDetails].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      // Handle numeric sorting for amount
      if (orderBy === 'amount') {
        aVal = Math.abs(parseFloat(aVal)) || 0;
        bVal = Math.abs(parseFloat(bVal)) || 0;
      } else {
        // String sorting
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredDetails, orderBy, order]);

  useEffect(() => {
    if (open && businessDate && storeId && glAccount) {
      fetchDetails();
    }
  }, [open, businessDate, storeId, glAccount]);

  const fetchDetails = async () => {
    if (!firebaseUser) return;

    try {
      setLoading(true);
      setError(null);

      const firebaseUserIdToken = await firebaseUser.getIdToken(true);

      const response = await axios.get('/hd-api/gl-export/cash-entry-details', {
        params: {
          businessDate,
          storeId,
          glAccount,
        },
        headers: {
          Authorization: `Bearer ${firebaseUserIdToken}`,
        },
      });

      setDetails(response.data.data || []);
    } catch (err) {
      console.error('Error fetching cash entry details:', err);
      setError('Failed to load cash entry details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '-';
    const absNum = Math.abs(num);
    const prefix = num < 0 ? '-' : '';
    return `${prefix}$${absNum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Get display name for cash type
  const formatCashType = (cashType) => {
    if (!cashType) return 'Unknown';
    return cashType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Get chip color based on cash type
  const getCashTypeColor = (cashType) => {
    if (!cashType) return 'default';
    const type = cashType.toUpperCase();
    if (type.includes('OVERAGE')) return 'success';
    if (type.includes('SHORTAGE')) return 'warning';
    if (type === 'CASH_IN') return 'success';
    if (type === 'CASH_OUT') return 'error';
    return 'default';
  };

  // Calculate totals
  const totals = useMemo(() => {
    return filteredDetails.reduce(
      (acc, d) => {
        const amount = parseFloat(d.amount) || 0;
        if (amount > 0) {
          acc.debits += amount;
        } else {
          acc.credits += Math.abs(amount);
        }
        acc.net += amount;
        return acc;
      },
      { debits: 0, credits: 0, net: 0 }
    );
  }, [filteredDetails]);

  const hasActiveFilters = Object.values(columnFilters).some((v) => v.trim() !== '');

  // Count by type
  const typeCounts = useMemo(() => {
    return details.reduce((acc, d) => {
      const type = d.cashType || 'Unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
  }, [details]);

  // Get category label based on GL account
  const getCategoryLabel = () => {
    if (glAccount === '670001') return 'Over/Short POS';
    if (glAccount === '101001') return 'Cash Collected';
    if (glAccount === '111101') return 'Cash Offsets';
    return `Cash Entries (${glAccount})`;
  };

  // Export to Excel
  const handleExport = () => {
    const exportData = sortedDetails.map((item) => ({
      'Type': item.cashType,
      'Reason': item.reason || item.payoutReason || '',
      'Amount': parseFloat(item.amount) || 0,
      'D/C': item.debitCredit,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cash Entries');
    XLSX.writeFile(wb, `cash-entries-${storeId}-${businessDate}.xlsx`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '80vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PointOfSaleIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="span">
            {getCategoryLabel()}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
            Store {storeId} - {businessDate} - GL {glAccount}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {Object.entries(typeCounts).slice(0, 3).map(([type, count]) => (
            <Chip
              key={type}
              label={`${count} ${formatCashType(type)}`}
              size="small"
              color={getCashTypeColor(type)}
              variant="outlined"
            />
          ))}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && details.length === 0 && (
          <Alert severity="info">
            No cash entry details found for this selection.
          </Alert>
        )}

        {!loading && !error && details.length > 0 && (
          <>
            <TableContainer sx={{ maxHeight: 400, userSelect: 'text' }} onCopy={handleCopy}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {showCashGuid && (
                      <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                        <TableSortLabel
                          active={orderBy === 'cashGuid'}
                          direction={orderBy === 'cashGuid' ? order : 'asc'}
                          onClick={() => handleRequestSort('cashGuid')}
                        >
                          Cash GUID
                        </TableSortLabel>
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'cashType'}
                        direction={orderBy === 'cashType' ? order : 'asc'}
                        onClick={() => handleRequestSort('cashType')}
                      >
                        Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === 'reason'}
                        direction={orderBy === 'reason' ? order : 'asc'}
                        onClick={() => handleRequestSort('reason')}
                      >
                        Reason
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="right">
                      <TableSortLabel
                        active={orderBy === 'amount'}
                        direction={orderBy === 'amount' ? order : 'asc'}
                        onClick={() => handleRequestSort('amount')}
                      >
                        Amount
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', borderBottom: 'none', pb: 0.5 }} align="center">
                      D/C
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    {showCashGuid && (
                      <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                        <TextField
                          size="small"
                          placeholder="Filter..."
                          value={columnFilters.cashGuid || ''}
                          onChange={(e) => handleColumnFilterChange('cashGuid', e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <FilterListIcon fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: columnFilters.cashGuid && (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => clearColumnFilter('cashGuid')}
                                  edge="end"
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{ width: '100%' }}
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        placeholder="Filter..."
                        value={columnFilters.cashType || ''}
                        onChange={(e) => handleColumnFilterChange('cashType', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters.cashType && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => clearColumnFilter('cashType')}
                                edge="end"
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: '100%' }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }}>
                      <TextField
                        size="small"
                        placeholder="Filter..."
                        value={columnFilters.reason || ''}
                        onChange={(e) => handleColumnFilterChange('reason', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters.reason && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => clearColumnFilter('reason')}
                                edge="end"
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: '100%' }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }} align="right">
                      <TextField
                        size="small"
                        placeholder="Filter..."
                        value={columnFilters.amount || ''}
                        onChange={(e) => handleColumnFilterChange('amount', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters.amount && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => clearColumnFilter('amount')}
                                edge="end"
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: '100%' }}
                      />
                    </TableCell>
                    <TableCell sx={{ backgroundColor: '#f5f5f5', pt: 0, pb: 1 }} align="center">
                      <TextField
                        size="small"
                        placeholder="Filter..."
                        value={columnFilters.debitCredit || ''}
                        onChange={(e) => handleColumnFilterChange('debitCredit', e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <FilterListIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: columnFilters.debitCredit && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => clearColumnFilter('debitCredit')}
                                edge="end"
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ width: '100%' }}
                      />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedDetails.map((detail, index) => (
                    <TableRow
                      key={`${detail.cashGuid}-${index}`}
                      sx={{
                        '&:hover': { backgroundColor: '#f5f5f5' },
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                      }}
                    >
                      {showCashGuid && (
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {detail.cashGuid || '-'}
                        </TableCell>
                      )}
                      <TableCell>
                        <Chip
                          label={formatCashType(detail.cashType)}
                          size="small"
                          color={getCashTypeColor(detail.cashType)}
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        {detail.reason || detail.payoutReason || <Typography variant="caption" color="text.secondary">N/A</Typography>}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 500,
                          color: parseFloat(detail.amount) < 0 ? 'error.main' : 'success.main',
                        }}
                      >
                        {formatCurrency(detail.amount)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={detail.debitCredit === 'D' ?
                            <TrendingUpIcon sx={{ fontSize: 14 }} /> :
                            <TrendingDownIcon sx={{ fontSize: 14 }} />
                          }
                          label={detail.debitCredit || '-'}
                          size="small"
                          color={detail.debitCredit === 'D' ? 'error' : 'success'}
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', minWidth: 50 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 2,
                pt: 2,
                borderTop: '1px solid #e0e0e0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredDetails.length} entr{filteredDetails.length !== 1 ? 'ies' : 'y'} found
                  {hasActiveFilters && ` (filtered from ${details.length})`}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={showCashGuid}
                      onChange={(e) => setShowCashGuid(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Show Cash GUID</Typography>}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2.5 }}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">
                    Debits
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: 'error.main' }}>
                    {formatCurrency(totals.debits)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">
                    Credits
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: 'success.main' }}>
                    {formatCurrency(totals.credits)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" color="text.secondary">
                    Net
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: totals.net >= 0 ? 'success.main' : 'error.main',
                    }}
                  >
                    {formatCurrency(totals.net)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExport}
          disabled={details.length === 0}
        >
          Export
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  );
};

export default CashEntryDetailsDialog;
